import { ECONOMY } from '../constants/economy-registry';
import { STREAK_REWARD_SOURCE_PREFIX } from '../constants/economy';
export const EMPTY_WIN_STREAK = {
    current: 0,
    best: 0,
    stepsPaid: [],
    savedOn: null,
    spentOn: null,
};
const { steps, repeatEvery } = ECONOMY.rewards.winStreak;
/** From here a streak is worth a warning, a save and a line when it ends (D3, D4). */
export const STREAK_STAKE_FROM = ECONOMY.sinks.streakSave[0].from;
const LAST_STEP = steps[steps.length - 1];
/**
 * What a step pays, or null if `n` isn't a step. Past the last step, every
 * `repeatEvery` wins pays the last step's chest again, under the last step's id.
 */
export function streakStepReward(n) {
    const repeats = n > LAST_STEP.at && (n - LAST_STEP.at) % repeatEvery === 0;
    const def = repeats ? LAST_STEP : steps.find((s) => s.at === n);
    if (!def)
        return null;
    return {
        step: n,
        coins: def.coins,
        tickets: def.tickets,
        sourceId: def.coins > 0 ? `${STREAK_REWARD_SOURCE_PREFIX}${def.at}` : null,
    };
}
/** The next step above `current`, for the win line and the streak card. */
export function nextStreakStep(current) {
    const fixed = steps.find((s) => s.at > current);
    if (fixed)
        return fixed.at;
    const past = current - LAST_STEP.at;
    return LAST_STEP.at + (Math.floor(past / repeatEvery) + 1) * repeatEvery;
}
/** The Streak Save's price at this streak, or 0 below the first band (no save). */
export function streakSavePrice(current) {
    let price = 0;
    for (const band of ECONOMY.sinks.streakSave)
        if (current >= band.from)
            price = band.coins;
    return price;
}
export function canSaveStreak(state, puzzle) {
    return streakSavePrice(state.current) > 0 && state.savedOn !== puzzle;
}
export function applyWinStreakEvent(state, evt) {
    const same = { state, reached: null, broke: 0 };
    switch (evt.type) {
        case 'saved':
            return { ...same, state: { ...state, savedOn: evt.puzzle } };
        case 'attemptEnded': {
            if (!evt.firstClear || evt.revealed)
                return same;
            return {
                state: { ...state, current: 0, stepsPaid: [], spentOn: evt.puzzle },
                reached: null,
                broke: state.current,
            };
        }
        case 'win': {
            if (!evt.firstClear || evt.revealed || state.spentOn === evt.puzzle)
                return same;
            const current = state.current + 1;
            const isNewStep = streakStepReward(current) !== null && !state.stepsPaid.includes(current);
            return {
                state: {
                    ...state,
                    current,
                    best: Math.max(state.best, current),
                    stepsPaid: isNewStep ? [...state.stepsPaid, current] : state.stepsPaid,
                },
                reached: isNewStep ? current : null,
                broke: 0,
            };
        }
    }
}
const count = (v) => Number.isInteger(v) && v > 0 ? v : 0;
const label = (v) => (typeof v === 'string' && v.length > 0 ? v : null);
/** The save's field, validated: anything broken reads as a fresh streak, never a crash. */
export function winStreakFromRaw(raw) {
    if (raw === null || typeof raw !== 'object')
        return undefined;
    const r = raw;
    const current = count(r.current);
    const stepsPaid = Array.isArray(r.stepsPaid)
        ? [...new Set(r.stepsPaid.map(count).filter((n) => n > 0 && n <= current))]
        : [];
    return {
        current,
        best: Math.max(count(r.best), current),
        stepsPaid,
        savedOn: label(r.savedOn),
        spentOn: label(r.spentOn),
    };
}
